import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Empty, Row, Spin } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PRIMARY, WHITE } from '../../../constants/color';
import { SHIPPING_METHOD } from '../../../constants/generalConstants';
import { GENERAL_TIMEOUT } from '../../../constants/systemConstants';
import { GoodData } from '../../../types';
import { alertMessage } from '../../../utils/alertMessage';
import GoodCard from '../common/GoodCard';

/** Card height with the select button under the price */
const CARD_HEIGHT = 270;
/** Tap-to-proceed cards have no button, so they need less room */
const CARD_HEIGHT_NO_FOOTER = 232;

type SelectGoodsGalleryGridProps = {
  goodData: GoodData[];
  loading: boolean;
  showSelected: boolean;
  selectedGoodObjs: GoodData[];
  setSelectedGoodObjs: React.Dispatch<React.SetStateAction<GoodData[]>>;
  multiSelectFunct: boolean;
  shippingTransportId?: number;
  hideSKU?: boolean;
  /**
   * When set, single-select skips staging: tapping a card proceeds to the next
   * step right away. Only passed by flows where that step can still be
   * cancelled (see `tapToProceed` in SelectGoodsModal).
   */
  onSingleSelect?: (good: GoodData) => void;
};

const SelectGoodsGalleryGrid = ({
  goodData,
  loading,
  showSelected,
  selectedGoodObjs,
  setSelectedGoodObjs,
  multiSelectFunct,
  shippingTransportId,
  hideSKU,
  onSingleSelect,
}: SelectGoodsGalleryGridProps) => {
  const { t } = useTranslation();
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();

  /** Debounced warning so rapid taps don't stack up messages */
  const warn = (message: string) => {
    if (typingTimeout) clearTimeout(typingTimeout);
    setTypingTimeout(
      setTimeout(() => alertMessage('warning', t(message)), GENERAL_TIMEOUT)
    );
  };

  const isDisabled = (goodObj: GoodData) =>
    !!(goodObj.isDelete || (hideSKU && goodObj.hasGoodsSkuAttr));

  /** Tap-to-proceed: validate the pick, then hand it straight to the next step */
  const onTap = (goodObj: GoodData) => {
    if (
      shippingTransportId &&
      goodObj.shippingMethod === SHIPPING_METHOD.SHIPPING &&
      shippingTransportId !== goodObj.shippingTransportId
    ) {
      warn('order.alerts.shippingMismatchWarning');
      return;
    }
    onSingleSelect && onSingleSelect(goodObj);
  };

  /** Adds a good in single-select mode, warning when the pick isn't allowed */
  const selectSingle = (goodObj: GoodData, checkTransport: boolean) => {
    if (checkTransport && shippingTransportId) {
      if (
        goodObj.shippingMethod === SHIPPING_METHOD.SHIPPING &&
        shippingTransportId !== goodObj.shippingTransportId
      ) {
        warn('order.alerts.shippingMismatchWarning');
      } else if (selectedGoodObjs.length) {
        warn('goods.alerts.canOnlySelectOneGood');
      } else {
        setSelectedGoodObjs((prev) => prev.concat(goodObj));
      }
      return;
    }
    if (selectedGoodObjs.length && selectedGoodObjs[0] !== goodObj) {
      warn('goods.alerts.canOnlySelectOneGood');
    } else {
      selectedGoodObjs.length < 1 &&
        setSelectedGoodObjs((prev) => prev.concat(goodObj));
    }
  };

  const selectButton = (
    goodObj: GoodData,
    { text, checkTransport }: { text?: boolean; checkTransport?: boolean }
  ) => (
    <Button
      size="small"
      icon={<PlusOutlined />}
      type={text ? 'text' : undefined}
      disabled={
        !!(
          (!text && goodObj.isDelete) ||
          (hideSKU && goodObj.hasGoodsSkuAttr)
        )
      }
      style={text ? { color: WHITE, backgroundColor: PRIMARY } : undefined}
      onClick={() => {
        if (multiSelectFunct) {
          setSelectedGoodObjs((prev) => prev.concat(goodObj));
        } else {
          selectSingle(goodObj, !!checkTransport);
        }
      }}
    >
      {t('general.selectLabel')}
    </Button>
  );

  const deselectButton = (onClick: () => void, text?: boolean) => (
    <Button
      onClick={onClick}
      size="small"
      icon={<MinusOutlined />}
      type={text ? 'text' : undefined}
      style={{ color: WHITE, backgroundColor: PRIMARY }}
    >
      {t('general.selectedLabel')}
    </Button>
  );

  return (
    <Spin spinning={loading}>
      {!showSelected && !goodData.length && <Empty></Empty>}
      <Row
        gutter={[16, 8]}
        style={{
          // Grow to fit the cards; only scroll when the viewport can't hold them
          maxHeight: 'calc(100vh - 340px)',
          overflow: 'auto',
        }}
      >
        {showSelected
          ? selectedGoodObjs.map((goodObj, index) => (
              <Col key={index} span={12} sm={8} md={6} lg={4} xxl={3}>
                <GoodCard
                  good={goodObj}
                  height={CARD_HEIGHT}
                  imagePreview
                  footer={
                    selectedGoodObjs.indexOf(goodObj) > -1
                      ? deselectButton(
                          () =>
                            setSelectedGoodObjs(
                              selectedGoodObjs.filter((x) => x !== goodObj)
                            ),
                          true
                        )
                      : selectButton(goodObj, { text: true })
                  }
                />
              </Col>
            ))
          : goodData.map((goodObj, index) => (
              <Col key={index} span={12} sm={8} md={6} lg={4} xxl={3}>
                {onSingleSelect ? (
                  // Tap the card to go straight to the next step: no select
                  // button, so the card is shorter. Image preview is off here,
                  // otherwise tapping the picture would open the large image
                  // instead of proceeding.
                  <GoodCard
                    good={goodObj}
                    height={CARD_HEIGHT_NO_FOOTER}
                    disabled={isDisabled(goodObj)}
                    onClick={() => onTap(goodObj)}
                  />
                ) : (
                  <GoodCard
                    good={goodObj}
                    height={CARD_HEIGHT}
                    imagePreview
                    footer={
                      selectedGoodObjs.findIndex(
                        (obj) => obj.goodsId === goodObj.goodsId
                      ) !== -1
                        ? deselectButton(() => {
                            const tempList = selectedGoodObjs.slice();
                            const i = tempList.findIndex(
                              (obj) => obj.goodsId === goodObj.goodsId
                            );
                            if (i !== -1) tempList.splice(i, 1);
                            setSelectedGoodObjs(tempList);
                          })
                        : selectButton(goodObj, { checkTransport: true })
                    }
                  />
                )}
              </Col>
            ))}
      </Row>
    </Spin>
  );
};

export default SelectGoodsGalleryGrid;
